import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  clearFlightRecorder,
  configureFlightRecorder,
  flightEventsAsRedactedJsonLines,
  FLOW_CAPACITY,
  getFlightRecorderSnapshot,
  subscribeFlightRecorder,
} from './flightRecorder'
import { redactFlightEvent, redactValue } from './flightRedact'
import styles from './FlightRecorderPanel.module.scss'

const CATS = [
  { id: 'HTTP', prefix: 'HTTP:' },
  { id: 'WS', prefix: 'WS:' },
  { id: 'SEAT', prefix: 'SEAT:' },
  { id: 'BOOKING', prefix: 'BOOKING:' },
  { id: 'PAYMENT', prefix: 'PAYMENT:' },
  { id: 'NAV', prefix: 'NAV:' },
  { id: 'ATOM', prefix: 'ATOM' },
  { id: 'MODAL', prefix: 'MODAL:' },
  { id: 'MODAL_VIEW', prefix: 'MODAL_VIEW:' },
  { id: 'PRINT', prefix: 'PRINT:' },
  { id: 'ERROR', prefix: 'ERROR:' },
  { id: 'USER', prefix: 'USER:' },
  { id: 'IDLE', prefix: 'IDLE:' },
  { id: 'SAVER', prefix: 'SAVER:' },
  { id: 'RECORDER', prefix: 'RECORDER:' },
  { id: 'TIMER', prefix: 'TIMER:' },
]

const FLOW_ON = {
  HTTP: true,
  WS: true,
  SEAT: true,
  BOOKING: true,
  PAYMENT: true,
  NAV: true,
  ATOM: true,
  MODAL: true,
  MODAL_VIEW: true,
  PRINT: true,
  ERROR: true,
  USER: false,
  IDLE: false,
  SAVER: false,
  RECORDER: true,
  TIMER: false,
}

const IDLE_ON = {
  HTTP: false,
  WS: false,
  SEAT: false,
  BOOKING: false,
  PAYMENT: false,
  NAV: true,
  ATOM: true,
  MODAL: true,
  MODAL_VIEW: true,
  PRINT: false,
  ERROR: true,
  USER: true,
  IDLE: true,
  SAVER: true,
  RECORDER: true,
  TIMER: true,
}

const ALL_ON = Object.fromEntries(CATS.map((c) => [c.id, true]))

const DEFAULT_CAPACITY = 200
const SLOW_MS_DEFAULT = 100
const PANEL_MIN_W = 420
const PANEL_MIN_H = 360
const PANEL_DEFAULT_W = 560
const PANEL_DEFAULT_H = 560
const PANEL_MARGIN = 12
const PANEL_SIZE_KEY = '__flight_recorder_panel_size'
const DETAIL_WIDTH_KEY = '__flight_recorder_detail_width'
const DETAIL_MIN_W = 200
const DETAIL_DEFAULT_W = 280
const LIST_MIN_W = 180
const SPLIT_HANDLE_W = 28

function readStoredPanelSize () {
  try {
    const raw = sessionStorage.getItem(PANEL_SIZE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const w = Number(parsed?.width)
    const h = Number(parsed?.height)
    if (!Number.isFinite(w) || !Number.isFinite(h)) return null
    return { width: w, height: h }
  } catch {
    return null
  }
}

function writeStoredPanelSize (size) {
  try {
    sessionStorage.setItem(PANEL_SIZE_KEY, JSON.stringify(size))
  } catch {
    // ignore
  }
}

function readStoredDetailWidth () {
  try {
    const n = Number(sessionStorage.getItem(DETAIL_WIDTH_KEY))
    return Number.isFinite(n) && n >= DETAIL_MIN_W ? n : null
  } catch {
    return null
  }
}

function writeStoredDetailWidth (width) {
  try {
    sessionStorage.setItem(DETAIL_WIDTH_KEY, String(Math.round(width)))
  } catch {
    // ignore
  }
}

function clampDetailWidth (width, panelWidth) {
  const pw = Number(panelWidth) || PANEL_DEFAULT_W
  const max = Math.max(DETAIL_MIN_W, pw - LIST_MIN_W - SPLIT_HANDLE_W - 16)
  return clamp(width, DETAIL_MIN_W, max)
}

function clampPanelSize (width, height) {
  if (typeof window === 'undefined') {
    return {
      width: clamp(width, PANEL_MIN_W, PANEL_DEFAULT_W),
      height: clamp(height, PANEL_MIN_H, PANEL_DEFAULT_H),
    }
  }
  const maxW = Math.max(PANEL_MIN_W, window.innerWidth - PANEL_MARGIN * 2)
  const maxH = Math.max(PANEL_MIN_H, window.innerHeight - PANEL_MARGIN * 2)
  return {
    width: clamp(width, PANEL_MIN_W, maxW),
    height: clamp(height, PANEL_MIN_H, maxH),
  }
}

function colorFor (type) {
  if (type.startsWith('HTTP:')) return '#34d399'
  if (type.startsWith('WS:')) return '#2dd4bf'
  if (type.startsWith('SEAT:')) return '#fbbf24'
  if (type.startsWith('BOOKING:')) return '#fb923c'
  if (type.startsWith('PAYMENT:')) return '#4ade80'
  if (type.startsWith('SAVER:')) return '#f97316'
  if (type.startsWith('IDLE:') || type.startsWith('TIMER:')) return '#38bdf8'
  if (type.startsWith('MODAL:') || type.startsWith('MODAL_VIEW:')) return '#a78bfa'
  if (type.startsWith('PRINT:')) return '#22c55e'
  if (type.startsWith('NAV:')) return '#eab308'
  if (type.startsWith('ATOM')) return '#f472b6'
  if (type.startsWith('USER:')) return '#e5e7eb'
  if (type.startsWith('ERROR:')) return '#ef4444'
  return '#94a3b8'
}

function isSuspicious (e, eventsByRequestId) {
  const t = e.type || ''
  if (t === 'USER:INPUT_IGNORED') return true
  if (t.startsWith('ERROR:')) return true
  if (t === 'HTTP:ERR') return true
  if (t.startsWith('PRINT:') && String(e?.meta?.code || '').toUpperCase() === 'TIMEOUT') return true
  if (t === 'SAVER:SHOW' && e?.meta?.modalDestroyed) return true
  // REQ without RES (still pending) older than ~60s → suspect
  if (t === 'HTTP:REQ' && e?.meta?.requestId) {
    const pair = eventsByRequestId.get(e.meta.requestId)
    if (pair?.req && !pair?.res && !pair?.err && e.p != null) {
      const age = (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()) - e.p
      if (age > 60000) return true
    }
  }
  // HTTP:ERR on block after modal_open in same flowSpan
  if (t === 'HTTP:ERR' && e?.meta?.kind === 'block' && e?.meta?.flowSpanId) {
    return true
  }
  return false
}

function clamp (n, min, max) {
  return Math.min(max, Math.max(min, n))
}

function shortLabel (e) {
  const m = e.meta || {}
  if (e.type?.startsWith('HTTP:')) {
    const dur = m.durationMs != null ? ` ${m.durationMs}ms` : ''
    const st = m.status != null ? ` ${m.status}` : ''
    return `${m.label || m.kind || ''} ${m.method || ''} ${m.urlPath || ''}${st}${dur}`.trim()
  }
  if (e.type?.startsWith('WS:')) {
    return `${m.event || ''} ${m.seatId || m.topic || ''}`.trim()
  }
  if (e.type?.startsWith('SEAT:')) {
    return `${m.label || m.seatId || ''} ${m.syncEntryId ? `sync=${String(m.syncEntryId).slice(0, 8)}…` : ''}`.trim()
  }
  if (e.type === 'NAV:ROUTE') {
    return `${m.from || ''} → ${m.to || m.pathname || ''}`.trim()
  }
  return ''
}

async function copyText (text) {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.focus()
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      return ok
    } catch {
      return false
    }
  }
}

function downloadJson (filename, obj) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function truncateId (value, head = 8, tail = 4) {
  const s = value == null ? '' : String(value)
  if (s.length <= head + tail + 1) return s
  return `${s.slice(0, head)}…${s.slice(-tail)}`
}

function CopyChip ({ label, value }) {
  const [ok, setOk] = useState(false)
  if (value == null || value === '') return null
  const full = String(value)
  return (
    <button
      type="button"
      className={styles.copyChip}
      title={full}
      onClick={async (e) => {
        e.stopPropagation()
        const copied = await copyText(full)
        setOk(copied)
        setTimeout(() => setOk(false), 700)
      }}
    >
      <span className={styles.copyChipLabel}>{label}</span>
      <span className={styles.copyChipValue}>{ok ? 'copied' : truncateId(full)}</span>
    </button>
  )
}

function OverviewRow ({ k, v, mono }) {
  if (v == null || v === '') return null
  return (
    <div className={styles.overviewRow}>
      <span className={styles.overviewKey}>{k}</span>
      <span className={mono ? styles.overviewMono : styles.overviewVal}>{String(v)}</span>
    </div>
  )
}

function JsonNode ({ name, value, depth = 0, defaultOpen }) {
  const isObj = value !== null && typeof value === 'object'
  const isArr = Array.isArray(value)
  const [open, setOpen] = useState(
    defaultOpen != null ? defaultOpen : depth < 1,
  )

  if (!isObj) {
    let cls = styles.jsonNull
    let text = 'null'
    if (typeof value === 'string') {
      cls = styles.jsonStr
      text = JSON.stringify(value)
    } else if (typeof value === 'number') {
      cls = styles.jsonNum
      text = String(value)
    } else if (typeof value === 'boolean') {
      cls = styles.jsonBool
      text = String(value)
    }
    return (
      <div className={styles.jsonLine} style={{ paddingLeft: depth * 12 }}>
        {name != null && <span className={styles.jsonKey}>{name}: </span>}
        <span className={cls}>{text}</span>
      </div>
    )
  }

  const keys = isArr ? value.map((_, i) => i) : Object.keys(value)
  const summary = isArr ? `Array(${value.length})` : `Object(${keys.length})`

  return (
    <div className={styles.jsonNode}>
      <button
        type="button"
        className={styles.jsonToggle}
        style={{ paddingLeft: depth * 12 }}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.jsonCaret}>{open ? '▾' : '▸'}</span>
        {name != null && <span className={styles.jsonKey}>{name}: </span>}
        <span className={styles.jsonSummary}>{summary}</span>
      </button>
      {open &&
        keys.map((k) => (
          <JsonNode
            key={String(k)}
            name={isArr ? String(k) : k}
            value={isArr ? value[k] : value[k]}
            depth={depth + 1}
            defaultOpen={false}
          />
        ))}
    </div>
  )
}

function JsonTree ({ value, defaultOpenRoot = true }) {
  if (value == null) {
    return <div className={styles.jsonEmpty}>—</div>
  }
  return (
    <div className={styles.jsonTree}>
      <JsonNode name={null} value={value} depth={0} defaultOpen={defaultOpenRoot} />
    </div>
  )
}

function pickSeatFields (meta) {
  const body = meta?.requestBody || meta?.responseBody || {}
  const nested =
    (body && typeof body === 'object' && body.data && typeof body.data === 'object'
      ? body.data
      : null) || {}
  return {
    seat: body.seat ?? nested.seat ?? meta?.seatLabel ?? null,
    row: body.row ?? nested.row ?? null,
    col: body.col ?? nested.col ?? null,
    seatId: meta?.seatId || body.seatId || nested.seatId || null,
    fareId: meta?.fareId !== undefined ? meta.fareId : (body.fareId ?? nested.fareId ?? null),
    syncEntryId: meta?.syncEntryId || nested.syncEntryId || body.syncEntryId || null,
    step: body.step || nested.step || null,
  }
}

function EventDetail ({
  selected,
  verbose,
  waterfall,
  paired,
  width,
}) {
  const isHttp = String(selected?.type || '').startsWith('HTTP:')
  const [tab, setTab] = useState(isHttp ? 'overview' : 'raw')
  const meta = useMemo(() => {
    if (!selected?.meta) return null
    // Detail pane: keep structure (truncate), not keys-only — easier to read than flat JSON dump
    return redactValue(selected.meta, {
      verbose,
      bodyMode: verbose ? 'full' : 'truncate',
    })
  }, [selected, verbose])

  useEffect(() => {
    setTab(String(selected?.type || '').startsWith('HTTP:') ? 'overview' : 'raw')
  }, [selected?.id])

  const seat = pickSeatFields(selected?.meta || {})
  const pairResRaw = paired?.res?.meta || paired?.err?.meta || null
  const pairReqRaw = paired?.req?.meta || null
  const reqMeta = useMemo(() => {
    const raw =
      selected?.type === 'HTTP:REQ'
        ? selected.meta
        : pairReqRaw || selected?.meta
    if (!raw) return null
    return redactValue(raw, { verbose, bodyMode: verbose ? 'full' : 'truncate' })
  }, [selected, pairReqRaw, verbose])

  const effectiveRes = useMemo(() => {
    const raw =
      selected?.type === 'HTTP:RES' || selected?.type === 'HTTP:ERR'
        ? selected.meta
        : pairResRaw
    if (!raw) return null
    return redactValue(raw, { verbose, bodyMode: verbose ? 'full' : 'truncate' })
  }, [selected, pairResRaw, verbose])

  const status =
    meta?.status ??
    effectiveRes?.status ??
    (selected?.type === 'HTTP:ERR' ? meta?.status : null)
  const durationMs =
    meta?.durationMs ??
    effectiveRes?.durationMs ??
    null

  const tabs = isHttp
    ? [
        { id: 'overview', label: 'Overview' },
        { id: 'req', label: 'Req' },
        { id: 'res', label: 'Res' },
        { id: 'raw', label: 'Raw' },
      ]
    : [
        { id: 'overview', label: 'Overview' },
        { id: 'raw', label: 'Raw' },
      ]

  return (
    <div
      data-fr-nodrag
      className={styles.detailPane}
      style={{ width, flex: `0 0 ${width}px` }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{selected.type}</div>
      <div style={{ opacity: 0.7, marginBottom: 8, fontSize: 10 }}>
        #{selected.id} · {selected.t}
        {selected.source ? ` · ${selected.source}` : ''}
      </div>

      <div className={styles.tabRow}>
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={styles.tabBtn}
            data-active={tab === t.id ? 'true' : 'false'}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className={styles.overviewBlock}>
          <OverviewRow k="method" v={meta?.method} />
          <OverviewRow k="label" v={meta?.label || meta?.kind} />
          <OverviewRow k="status" v={status} />
          <OverviewRow k="duration" v={durationMs != null ? `${durationMs}ms` : null} />
          <OverviewRow k="step" v={seat.step} />
          <OverviewRow k="seat" v={seat.seat} />
          <OverviewRow k="row / col" v={seat.row != null || seat.col != null ? `${seat.row ?? '—'} / ${seat.col ?? '—'}` : null} />
          <OverviewRow
            k="fareId"
            v={seat.fareId === null ? 'null' : seat.fareId}
            mono
          />
          <div className={styles.chipRow}>
            <CopyChip label="session" value={meta?.sessionId} />
            <CopyChip label="req" value={meta?.requestId} />
            <CopyChip label="span" value={meta?.flowSpanId} />
            <CopyChip label="seatId" value={seat.seatId} />
            <CopyChip label="sync" value={seat.syncEntryId} />
          </div>
          {waterfall.length > 1 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Span waterfall</div>
              {waterfall.map((w) => (
                <div key={w.id} style={{ fontSize: 10, opacity: 0.85 }}>
                  +{w.deltaMs ?? '?'}ms {w.type}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'req' && (
        <div>
          <OverviewRow k="url" v={reqMeta?.urlPath || meta?.urlPath} mono />
          <div style={{ fontWeight: 600, margin: '6px 0 4px' }}>requestBody</div>
          <JsonTree
            value={(reqMeta || meta)?.requestBody ?? null}
            defaultOpenRoot
          />
        </div>
      )}

      {tab === 'res' && (
        <div>
          <OverviewRow k="status" v={effectiveRes?.status ?? status} />
          <OverviewRow
            k="duration"
            v={
              (effectiveRes?.durationMs ?? durationMs) != null
                ? `${effectiveRes?.durationMs ?? durationMs}ms`
                : null
            }
          />
          <div style={{ fontWeight: 600, margin: '6px 0 4px' }}>responseBody</div>
          <JsonTree
            value={effectiveRes?.responseBody ?? meta?.responseBody ?? null}
            defaultOpenRoot
          />
        </div>
      )}

      {tab === 'raw' && (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Meta</div>
          <JsonTree value={meta} defaultOpenRoot={false} />
        </div>
      )}
    </div>
  )
}

export function FlightRecorderPanel ({
  open,
  onClose,
  contextState,
}) {
  const [frozen, setFrozen] = useState(false)
  const [minimized, setMinimized] = useState(false)
  const [search, setSearch] = useState('')
  const [preset, setPreset] = useState('flow')
  const [cats, setCats] = useState(() => ({ ...FLOW_ON }))
  const [version, setVersion] = useState(0)
  const [copied, setCopied] = useState(false)
  const [verbose, setVerbose] = useState(false)
  const [slowOnly, setSlowOnly] = useState(false)
  const [slowMs, setSlowMs] = useState(SLOW_MS_DEFAULT)
  const [selectedId, setSelectedId] = useState(null)
  const [panelPos, setPanelPos] = useState({ left: 12, top: 12 })
  const [panelSize, setPanelSize] = useState(() => {
    const stored = typeof window !== 'undefined' ? readStoredPanelSize() : null
    return clampPanelSize(
      stored?.width || PANEL_DEFAULT_W,
      stored?.height || PANEL_DEFAULT_H,
    )
  })
  const [detailWidth, setDetailWidth] = useState(() => {
    const stored = typeof window !== 'undefined' ? readStoredDetailWidth() : null
    return stored || DETAIL_DEFAULT_W
  })
  const [dragging, setDragging] = useState(false)
  const [resizing, setResizing] = useState(false)
  const [splitting, setSplitting] = useState(false)
  const scrollerRef = useRef(null)
  const panelRef = useRef(null)
  const dragRef = useRef({
    active: false,
    pointerId: null,
    originX: 0,
    originY: 0,
    startLeft: 0,
    startTop: 0,
  })
  const resizeRef = useRef({
    active: false,
    pointerId: null,
    originX: 0,
    originY: 0,
    startW: PANEL_DEFAULT_W,
    startH: PANEL_DEFAULT_H,
  })
  const splitRef = useRef({
    active: false,
    pointerId: null,
    originX: 0,
    startDetailW: DETAIL_DEFAULT_W,
  })

  useEffect(() => {
    if (!open) return
    const unsub = subscribeFlightRecorder(() => {
      if (!frozen) setVersion((v) => v + 1)
    })
    return () => unsub()
  }, [open, frozen])

  useEffect(() => {
    if (!open) return
    if (preset === 'flow') {
      configureFlightRecorder({ capacity: FLOW_CAPACITY })
      setCats({ ...FLOW_ON })
    } else if (preset === 'idle') {
      configureFlightRecorder({ capacity: DEFAULT_CAPACITY })
      setCats({ ...IDLE_ON })
    } else {
      configureFlightRecorder({ capacity: FLOW_CAPACITY })
      setCats({ ...ALL_ON })
    }
  }, [preset, open])

  const snapshot = useMemo(() => getFlightRecorderSnapshot(), [version])

  const eventsByRequestId = useMemo(() => {
    const map = new Map()
    for (const e of snapshot.events) {
      const rid = e?.meta?.requestId
      if (!rid) continue
      const cur = map.get(rid) || {}
      if (e.type === 'HTTP:REQ') cur.req = e
      if (e.type === 'HTTP:RES') cur.res = e
      if (e.type === 'HTTP:ERR') cur.err = e
      map.set(rid, cur)
    }
    return map
  }, [snapshot.events])

  const filtered = useMemo(() => {
    const q = String(search || '').trim().toLowerCase()
    const enabledPrefixes = CATS
      .filter((c) => cats[c.id])
      .map((c) => c.prefix)

    return snapshot.events.filter((e) => {
      const t = String(e.type || '')
      const catOk = enabledPrefixes.some((p) => (p === 'ATOM' ? t.startsWith('ATOM') : t.startsWith(p)))
      if (!catOk) return false
      if (slowOnly) {
        const dur = e?.meta?.durationMs
        if (!(typeof dur === 'number' && dur >= slowMs)) return false
      }
      if (!q) return true
      const hay = `${e.t} ${e.type} ${e.source || ''} ${JSON.stringify(e.meta || {})}`.toLowerCase()
      return hay.includes(q)
    })
  }, [snapshot.events, search, cats, slowOnly, slowMs])

  const selected = useMemo(
    () => filtered.find((e) => e.id === selectedId) || snapshot.events.find((e) => e.id === selectedId) || null,
    [filtered, snapshot.events, selectedId],
  )

  const waterfall = useMemo(() => {
    if (!selected?.meta?.flowSpanId) return []
    const span = selected.meta.flowSpanId
    const group = snapshot.events.filter((e) => e?.meta?.flowSpanId === span)
    if (!group.length) return []
    const p0 = group[0].p
    return group.map((e) => ({
      id: e.id,
      type: e.type,
      deltaMs: e.p != null && p0 != null ? Math.round((e.p - p0) * 1000) / 1000 : null,
    }))
  }, [selected, snapshot.events])

  useEffect(() => {
    if (!open || frozen || minimized) return
    const el = scrollerRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [open, frozen, minimized, filtered.length])

  useEffect(() => {
    if (!open || typeof window === 'undefined') return
    const size = clampPanelSize(panelSize.width, panelSize.height)
    setPanelSize(size)
    setPanelPos({
      left: Math.max(PANEL_MARGIN, window.innerWidth - size.width - PANEL_MARGIN),
      top: Math.max(PANEL_MARGIN, window.innerHeight - size.height - PANEL_MARGIN),
    })
    // Only on open — don't reset size every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open || typeof window === 'undefined') return
    const onWinResize = () => {
      setPanelSize((s) => {
        const next = clampPanelSize(s.width, s.height)
        writeStoredPanelSize(next)
        return next
      })
      setPanelPos((p) => {
        const maxL = Math.max(PANEL_MARGIN, window.innerWidth - panelSize.width - PANEL_MARGIN)
        const maxT = Math.max(PANEL_MARGIN, window.innerHeight - panelSize.height - PANEL_MARGIN)
        return {
          left: clamp(p.left, PANEL_MARGIN, maxL),
          top: clamp(p.top, PANEL_MARGIN, maxT),
        }
      })
    }
    window.addEventListener('resize', onWinResize)
    return () => window.removeEventListener('resize', onWinResize)
  }, [open, panelSize.width, panelSize.height])

  const onPanelPointerDown = (e) => {
    if (typeof e.target?.closest !== 'function') return
    if (e.target.closest('[data-fr-nodrag]')) return
    if (e.target.closest('[data-fr-resize]')) return
    if (e.target.closest('[data-fr-split]')) return
    if (e.button !== 0 && e.pointerType === 'mouse') return
    const el = panelRef.current
    if (!el) return
    dragRef.current = {
      active: true,
      pointerId: e.pointerId,
      originX: e.clientX,
      originY: e.clientY,
      startLeft: panelPos.left,
      startTop: panelPos.top,
    }
    setDragging(true)
    try {
      el.setPointerCapture(e.pointerId)
    } catch {
      // ignore
    }
  }

  const onPanelPointerMove = (e) => {
    const s = splitRef.current
    if (s.active && e.pointerId === s.pointerId) {
      // Dragging the split: moving left increases detail width
      const dx = s.originX - e.clientX
      const next = clampDetailWidth(s.startDetailW + dx, panelSize.width)
      setDetailWidth(next)
      return
    }
    const r = resizeRef.current
    if (r.active && e.pointerId === r.pointerId) {
      const dx = e.clientX - r.originX
      const dy = e.clientY - r.originY
      const next = clampPanelSize(r.startW + dx, r.startH + dy)
      setPanelSize(next)
      setDetailWidth((w) => clampDetailWidth(w, next.width))
      setPanelPos((p) => {
        const maxL = Math.max(PANEL_MARGIN, window.innerWidth - next.width - PANEL_MARGIN)
        const maxT = Math.max(PANEL_MARGIN, window.innerHeight - next.height - PANEL_MARGIN)
        return {
          left: clamp(p.left, PANEL_MARGIN, maxL),
          top: clamp(p.top, PANEL_MARGIN, maxT),
        }
      })
      return
    }
    const d = dragRef.current
    if (!d.active || e.pointerId !== d.pointerId) return
    if (typeof window === 'undefined') return
    const dx = e.clientX - d.originX
    const dy = e.clientY - d.originY
    const maxL = Math.max(PANEL_MARGIN, window.innerWidth - panelSize.width - PANEL_MARGIN)
    const maxT = Math.max(PANEL_MARGIN, window.innerHeight - panelSize.height - PANEL_MARGIN)
    setPanelPos({
      left: clamp(d.startLeft + dx, PANEL_MARGIN, maxL),
      top: clamp(d.startTop + dy, PANEL_MARGIN, maxT),
    })
  }

  const endDrag = (e) => {
    const s = splitRef.current
    if (s.active && (!e || e.pointerId === s.pointerId)) {
      const el = panelRef.current
      if (el && s.pointerId != null) {
        try {
          el.releasePointerCapture(s.pointerId)
        } catch {
          // ignore
        }
      }
      setDetailWidth((w) => {
        const next = clampDetailWidth(w, panelSize.width)
        writeStoredDetailWidth(next)
        return next
      })
      splitRef.current = {
        active: false,
        pointerId: null,
        originX: 0,
        startDetailW: DETAIL_DEFAULT_W,
      }
      setSplitting(false)
      return
    }
    const r = resizeRef.current
    if (r.active && (!e || e.pointerId === r.pointerId)) {
      const el = panelRef.current
      if (el && r.pointerId != null) {
        try {
          el.releasePointerCapture(r.pointerId)
        } catch {
          // ignore
        }
      }
      const measured = clampPanelSize(
        el?.offsetWidth || panelSize.width,
        el?.offsetHeight || panelSize.height,
      )
      setPanelSize(measured)
      writeStoredPanelSize(measured)
      setDetailWidth((w) => {
        const next = clampDetailWidth(w, measured.width)
        writeStoredDetailWidth(next)
        return next
      })
      resizeRef.current = {
        active: false,
        pointerId: null,
        originX: 0,
        originY: 0,
        startW: measured.width,
        startH: measured.height,
      }
      setResizing(false)
      return
    }
    const d = dragRef.current
    if (!d.active || (e && e.pointerId !== d.pointerId)) return
    const el = panelRef.current
    if (el && d.pointerId != null) {
      try {
        el.releasePointerCapture(d.pointerId)
      } catch {
        // ignore
      }
    }
    dragRef.current = { active: false, pointerId: null, originX: 0, originY: 0, startLeft: 0, startTop: 0 }
    setDragging(false)
  }

  const onResizePointerDown = (e) => {
    e.stopPropagation()
    e.preventDefault()
    if (e.button !== 0 && e.pointerType === 'mouse') return
    const el = panelRef.current
    resizeRef.current = {
      active: true,
      pointerId: e.pointerId,
      originX: e.clientX,
      originY: e.clientY,
      startW: panelSize.width,
      startH: panelSize.height,
    }
    setResizing(true)
    try {
      el?.setPointerCapture(e.pointerId)
    } catch {
      // ignore
    }
  }

  const onSplitPointerDown = (e) => {
    e.stopPropagation()
    e.preventDefault()
    if (e.button !== 0 && e.pointerType === 'mouse') return
    const el = panelRef.current
    splitRef.current = {
      active: true,
      pointerId: e.pointerId,
      originX: e.clientX,
      startDetailW: detailWidth,
    }
    setSplitting(true)
    try {
      el?.setPointerCapture(e.pointerId)
    } catch {
      // ignore
    }
  }

  if (!open) return null

  const headerState = contextState || {}
  const liveLabel = frozen ? 'PAUSED' : 'LIVE ●'
  const sessionShort = headerState.sessionId
    ? `${String(headerState.sessionId).slice(0, 8)}…`
    : '—'
  const routeLabel = [headerState.origin, headerState.destination].filter(Boolean).join('→') || '—'

  if (minimized) {
    return (
      <button
        type="button"
        className={styles.miniPill}
        style={{ left: panelPos.left, top: panelPos.top }}
        onClick={() => setMinimized(false)}
      >
        Flow {liveLabel}
      </button>
    )
  }

  return (
    <div
      ref={panelRef}
      className={styles.panel}
      data-dragging={dragging ? 'true' : 'false'}
      data-resizing={resizing ? 'true' : 'false'}
      onPointerDown={onPanelPointerDown}
      onPointerMove={onPanelPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      style={{
        position: 'fixed',
        left: panelPos.left,
        top: panelPos.top,
        width: panelSize.width,
        height: panelSize.height,
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: 10, borderBottom: '1px solid rgba(148,163,184,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div style={{ fontWeight: 700, color: '#e5e7eb', flex: 1, minWidth: 0 }}>
            Kiosk Flow Inspector
            <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 600, color: frozen ? '#fbbf24' : '#4ade80' }}>
              {liveLabel}
            </span>
            <span className={styles.panelHint}>
              Mover: título · Ventana: esquina ↘ · Detalle: barra ‖
            </span>
          </div>
          <div data-fr-nodrag style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setFrozen((v) => !v)} style={btnStyle}>{frozen ? 'Resume' : 'Pause'}</button>
            <button type="button" onClick={() => { clearFlightRecorder(); setVersion((v) => v + 1); setSelectedId(null) }} style={btnStyle}>Clear</button>
            <button
              type="button"
              onClick={async () => {
                const ok = await copyText(flightEventsAsRedactedJsonLines({ verbose }))
                setCopied(ok)
                setTimeout(() => setCopied(false), 900)
              }}
              style={btnStyle}
            >
              {copied ? 'Copied' : 'Copy JSONL'}
            </button>
            <button
              type="button"
              onClick={() => {
                const sess = headerState.sessionId || 'nosession'
                const date = new Date().toISOString().slice(0, 10)
                downloadJson(`kiosk-flow-${date}-${String(sess).slice(0, 8)}.json`, {
                  exportedAt: new Date().toISOString(),
                  context: redactValue(headerState, { verbose: false }),
                  events: redactFlightEvent(snapshot.events, { verbose }),
                })
              }}
              style={btnStyle}
            >
              Export
            </button>
            <button type="button" onClick={() => setMinimized(true)} style={btnStyle}>─</button>
            <button type="button" onClick={onClose} style={btnStyle}>Close</button>
          </div>
        </div>

        <div className={styles.stateRow} style={{ marginTop: 8, display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11 }}>
          <span>session: <b>{sessionShort}</b></span>
          <span>route: <b>{routeLabel}</b></span>
          <span>step: <b>{String(headerState.route || '—')}</b></span>
          <span>seat: <b>{String(headerState.seatLabel || headerState.seatId || '—')}</b></span>
          <span>fare: <b>{String(headerState.fareId || '—')}</b></span>
          <span>cart: <b>{String(headerState.cartId || '—')}</b></span>
          <span>pax: <b>{String(headerState.passengerCount ?? '—')}</b></span>
        </div>

        <div data-fr-nodrag style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {['flow', 'idle', 'all'].map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPreset(p)}
              style={{
                ...btnStyle,
                borderColor: preset === p ? '#38bdf8' : 'rgba(148,163,184,0.25)',
                background: preset === p ? 'rgba(56,189,248,0.2)' : btnStyle.background,
              }}
            >
              {p === 'flow' ? 'Flow' : p === 'idle' ? 'Idle' : 'All'}
            </button>
          ))}
          <label className={styles.filterLabel} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={verbose} onChange={() => setVerbose((v) => !v)} />
            Verbose
          </label>
          <label className={styles.filterLabel} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={slowOnly} onChange={() => setSlowOnly((v) => !v)} />
            slow≥
            <input
              type="number"
              value={slowMs}
              onChange={(e) => setSlowMs(Number(e.target.value) || SLOW_MS_DEFAULT)}
              style={{ width: 48, background: '#0f172a', color: '#e5e7eb', border: '1px solid #334155', borderRadius: 4 }}
            />
            ms
          </label>
          <input
            data-fr-nodrag
            className={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="search…"
            style={{ flex: 1, minWidth: 120 }}
          />
        </div>

        <div data-fr-nodrag style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 11 }}>
          {CATS.map((c) => (
            <label key={c.id} className={styles.filterLabel} style={{ display: 'inline-flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!cats[c.id]}
                onChange={() => setCats((prev) => ({ ...prev, [c.id]: !prev[c.id] }))}
              />
              <span>{c.id}</span>
            </label>
          ))}
        </div>
      </div>

      <div
        style={{ display: 'flex', flex: 1, minHeight: 0, paddingBottom: 28 }}
        data-splitting={splitting ? 'true' : 'false'}
      >
        <div
          ref={scrollerRef}
          data-fr-nodrag
          className={styles.scrollArea}
          style={{
            padding: 8,
            overflow: 'auto',
            flex: 1,
            minWidth: LIST_MIN_W,
            fontSize: 11,
            lineHeight: 1.35,
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ opacity: 0.8, color: '#e5e7eb' }}>No events (or filtered out). Use preset Flow + run booking.</div>
          ) : filtered.map((e) => {
            const suspicious = isSuspicious(e, eventsByRequestId)
            const color = colorFor(String(e.type || ''))
            const active = selectedId === e.id
            const label = shortLabel(e)
            return (
              <button
                type="button"
                key={e.id}
                onClick={() => setSelectedId(e.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '6px 8px',
                  borderRadius: 8,
                  marginBottom: 6,
                  border: suspicious
                    ? '1px solid rgba(239,68,68,0.7)'
                    : active
                      ? '1px solid rgba(56,189,248,0.8)'
                      : '1px solid rgba(148,163,184,0.15)',
                  background: suspicious ? 'rgba(239,68,68,0.08)' : active ? 'rgba(56,189,248,0.12)' : 'rgba(15,23,42,0.6)',
                  cursor: 'pointer',
                  color: '#e5e7eb',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ color: 'rgba(226,232,240,0.9)' }}>[{e.t}]</span>
                    <span style={{ color, fontWeight: 700 }}>{e.type}</span>
                    {suspicious && <span style={{ color: '#ef4444', fontWeight: 700 }}>SUSPECT</span>}
                  </div>
                  <div style={{ opacity: 0.75, color: '#94a3b8' }}>#{e.id}</div>
                </div>
                {label ? <div style={{ opacity: 0.85, marginTop: 2, color: '#cbd5e1' }}>{label}</div> : null}
                {e.meta?.flowSpanId && (
                  <div style={{ opacity: 0.55, marginTop: 2, color: '#94a3b8', fontSize: 10 }}>
                    span {String(e.meta.flowSpanId).slice(0, 18)}…
                  </div>
                )}
              </button>
            )
          })}
        </div>

        {selected && (
          <>
            <button
              type="button"
              data-fr-nodrag
              data-fr-split
              className={styles.splitHandle}
              aria-label="Ajustar ancho del detalle"
              title="Arrastra para ensanchar o estrechar el detalle"
              onPointerDown={onSplitPointerDown}
            >
              <span className={styles.splitBars} aria-hidden>
                <span />
                <span />
              </span>
              <span className={styles.splitLabel}>‖</span>
            </button>
            <EventDetail
              selected={selected}
              verbose={verbose}
              waterfall={waterfall}
              width={clampDetailWidth(detailWidth, panelSize.width)}
              paired={
                selected?.meta?.requestId
                  ? eventsByRequestId.get(selected.meta.requestId)
                  : null
              }
            />
          </>
        )}
      </div>

      <button
        type="button"
        data-fr-nodrag
        data-fr-resize
        className={styles.resizeHandle}
        aria-label="Agrandar ventana"
        title="Arrastra para agrandar (ancho y alto)"
        onPointerDown={onResizePointerDown}
      >
        <span className={styles.resizeGrip} aria-hidden>
          <span />
          <span />
          <span />
        </span>
        <span className={styles.resizeLabel}>Resize</span>
      </button>
    </div>
  )
}

const btnStyle = {
  background: 'rgba(15,23,42,0.9)',
  color: '#e5e7eb',
  border: '1px solid rgba(148,163,184,0.25)',
  borderRadius: 8,
  padding: '6px 8px',
  cursor: 'pointer',
  fontSize: 11,
}
